//#region src/media-tracks.ts
/**
* Mirrors the Shaka video and audio tracks of the loaded asset into the media element's `videoRenditions` /
* `audioTracks` lists, and wires user selection back to `engine.selectVideoTrack()` and `engine.selectAudioTrack()`.
*
* Shaka video tracks are a flattened view over the manifest's variants rather than a list of their own, so they are
* hung off a single `'main'` video track — the one the renditions of the asset that is playing are read from.
*
* Requires the media-tracks mixin (track-list infrastructure) to be applied earlier in the chain so the host exposes
* `addVideoTrack`, `videoRenditions`, and friends.
*/
function ShakaMediaTracksMixin(BaseClass) {
	class ShakaMediaTracks extends BaseClass {
		#src = "";
		#videoTracksKey = "";
		#audioTracksKey = "";
		#isAbrOff = false;
		constructor(...args) {
			super(...args);
			const { engine } = this;
			if (!engine) return;
			engine.addEventListener("trackschanged", this.#onTracksChanged);
			engine.addEventListener("audiotrackschanged", this.#onAudioTracksChanged);
			engine.addEventListener("variantchanged", this.#onVariantChanged);
			engine.addEventListener("adaptation", this.#onVariantChanged);
			engine.addEventListener("audiotrackchanged", this.#onAudioTrackChanged);
			this.addEventListener("sourcechange", this.#onSourceChange);
			this.videoRenditions.addEventListener("change", this.#switchRendition);
			this.audioTracks.addEventListener("change", this.#switchAudioTrack);
		}
		destroy() {
			const { engine } = this;
			engine?.removeEventListener("trackschanged", this.#onTracksChanged);
			engine?.removeEventListener("audiotrackschanged", this.#onAudioTracksChanged);
			engine?.removeEventListener("variantchanged", this.#onVariantChanged);
			engine?.removeEventListener("adaptation", this.#onVariantChanged);
			engine?.removeEventListener("audiotrackchanged", this.#onAudioTrackChanged);
			this.removeEventListener("sourcechange", this.#onSourceChange);
			this.videoRenditions.removeEventListener("change", this.#switchRendition);
			this.audioTracks.removeEventListener("change", this.#switchAudioTrack);
			this.#reset();
			super.destroy();
		}
		#onTracksChanged = () => {
			const { engine } = this;
			if (!engine) return;
			const videoTracks = engine.getVideoTracks();
			const key = videoTracksKey(videoTracks);
			if (key === this.#videoTracksKey) {
				let metadataChanged = false;
				for (const rendition of this.videoRenditions) {
					const track = videoTracks[Number(rendition.id)];
					if (!track) continue;
					const frameRate = track.frameRate ?? void 0;
					if (rendition.frameRate === frameRate) continue;
					rendition.frameRate = frameRate;
					metadataChanged = true;
				}
				if (metadataChanged) this.videoRenditions.dispatchEvent(new Event("change"));
				this.#onAudioTracksChanged();
				return;
			}
			this.#videoTracksKey = key;
			this.#removeVideoTracks();
			this.#restoreAbr();
			const videoTrack = this.addVideoTrack("main");
			videoTrack.selected = true;
			for (const [index, track] of videoTracks.entries()) {
				const rendition = videoTrack.addRendition("", track.width ?? void 0, track.height ?? void 0, track.codecs ?? void 0, track.bandwidth, track.frameRate ?? void 0);
				rendition.id = `${index}`;
			}
			this.#setActiveRendition(videoTracks);
			this.#onAudioTracksChanged();
		};
		#onAudioTracksChanged = () => {
			const { engine } = this;
			if (!engine) return;
			const audioTracks = engine.getAudioTracks();
			const key = audioTracksKey(audioTracks);
			if (key === this.#audioTracksKey) {
				let metadataChanged = false;
				for (const audioTrack of this.audioTracks) {
					const track = audioTracks[Number(audioTrack.id)];
					if (!track) continue;
					const kind = track.primary ? "main" : "alternative";
					if (audioTrack.kind === kind) continue;
					audioTrack.kind = kind;
					metadataChanged = true;
				}
				if (metadataChanged) this.audioTracks.dispatchEvent(new Event("change"));
				return;
			}
			this.#audioTracksKey = key;
			this.#removeAudioTracks();
			for (const [index, track] of audioTracks.entries()) {
				const audioTrack = this.addAudioTrack(track.primary ? "main" : "alternative", track.label ?? track.language, track.language);
				audioTrack.id = `${index}`;
				audioTrack.enabled = track.active;
			}
		};
		#onVariantChanged = () => {
			const { engine } = this;
			if (engine) this.#setActiveRendition(engine.getVideoTracks());
		};
		#onAudioTrackChanged = () => {
			const { engine } = this;
			if (!engine) return;
			const activeIndex = engine.getAudioTracks().findIndex((track) => track.active);
			for (const audioTrack of this.audioTracks) audioTrack.enabled = audioTrack.id === `${activeIndex}`;
		};
		#switchRendition = () => {
			const { engine } = this;
			if (!engine) return;
			const selected = [...this.videoRenditions].find((rendition) => rendition.selected);
			if (!selected?.id) {
				this.#restoreAbr();
				return;
			}
			const videoTrack = engine.getVideoTracks()[Number(selected.id)];
			if (!videoTrack) return;
			this.#isAbrOff = true;
			engine.configure({ abr: { enabled: false } });
			engine.selectVideoTrack(videoTrack);
		};
		#switchAudioTrack = () => {
			const { engine } = this;
			if (!engine) return;
			const enabledTracks = [...this.audioTracks].filter((track) => track.enabled);
			const audioTracks = engine.getAudioTracks();
			const selectedTrack = enabledTracks.find((track) => !audioTracks[Number(track.id)]?.active) ?? enabledTracks[0];
			if (!selectedTrack?.id) return;
			const audioTrack = audioTracks[Number(selectedTrack.id)];
			if (audioTrack && !audioTrack.active) engine.selectAudioTrack(audioTrack);
			for (const track of enabledTracks) if (track !== selectedTrack) track.enabled = false;
		};
		#onSourceChange = () => {
			const srcChanged = this.src !== this.#src;
			this.#src = this.src;
			if (srcChanged) {
				this.#reset();
				return;
			}
			if (this.#isAbrOff && this.#isEngineAbrOn()) this.#switchRendition();
		};
		#isEngineAbrOn() {
			return this.engine?.getConfiguration().abr?.enabled !== false;
		}
		#setActiveRendition(videoTracks) {
			const activeIndex = videoTracks.findIndex((track) => track.active);
			for (const rendition of this.videoRenditions) rendition.active = activeIndex >= 0 && rendition.id === `${activeIndex}`;
		}
		#reset() {
			this.#removeVideoTracks();
			this.#removeAudioTracks();
			this.#restoreAbr();
			this.#videoTracksKey = "";
			this.#audioTracksKey = "";
		}
		#restoreAbr() {
			if (!this.#isAbrOff) return;
			this.#isAbrOff = false;
			this.engine?.configure({ abr: { enabled: true } });
		}
		#removeVideoTracks() {
			for (const videoTrack of this.videoTracks) this.removeVideoTrack(videoTrack);
		}
		#removeAudioTracks() {
			for (const audioTrack of this.audioTracks) this.removeAudioTrack(audioTrack);
		}
	}
	return ShakaMediaTracks;
}
function videoTracksKey(tracks) {
	return tracks.map((track) => `${track.width}x${track.height}|${track.codecs}|${track.bandwidth}|${track.hdr}`).join();
}
function audioTracksKey(tracks) {
	return tracks.map((track) => `${track.language}|${track.label}|${track.roles}|${track.channelsCount}`).join();
}
//#endregion
export { ShakaMediaTracksMixin };
